import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
import head from 'lodash.head';
import StudentRecommendations from './StudentRecommendations';
import { STUDENT } from '../../../../constants/shared/CommonConstants';
import { extractObservationsFromStatus } from '../../../../utils/shared/instructionUtils';
import StudentListTable from '../../../studentListTable/StudentListTable';
import { findFormLrsGroupNumber, formLrsGroupName } from '../../../lrs/shared/lrsHelpers';

const { INFO, ID, NAME } = STUDENT;

const Students = ({
    studentsData,
    columns,
    groups,
    selectedGrade,
    observationsContent,
    GroupWidget,
    measureList,
    observationActivitiesMapper,
    staffId,
    lrsTrackingInfo,
    location,
    commonCellBuilderComponent,
    selectIndividualStudent,
    onHelpIconClick,
    selectObservation,
    sessionId,
    lastLocation,
    SecureResultBanner,
    SummerPeriodBanner,
    isSecure,
}) => {
    const defaultSelectedStudentId = get(head(studentsData), ID);
    const [selectedStudentId, setSelectedStudentId] = useState(defaultSelectedStudentId);

    useEffect(() => {
        setSelectedStudentId(defaultSelectedStudentId);
    }, [defaultSelectedStudentId]);

    const selectItem = id => {
        const selectedStudentGroupName = studentsData.find(item => get(item, ID) === id).groupName;
        const groupNumber = findFormLrsGroupNumber(groups, selectedStudentGroupName);

        setSelectedStudentId(id);
        selectIndividualStudent(id, formLrsGroupName(selectedStudentGroupName, groupNumber));
    };

    const selectedStudentData = studentsData.find(
        student => get(student, ID) === selectedStudentId,
    );
    const studentGroup = groups.find(group => group.name === get(selectedStudentData, 'groupName'));
    const additionalActivities = get(selectedStudentData, 'additionalActivities', []);
    const additionalGroupData = get(selectedStudentData, 'additionalGroupData', {});
    const additionalGroup = isEmpty(additionalGroupData.groupName) ? null : (
        <GroupWidget groupData={additionalGroupData} location={location} />
    );
    const mainGroup = get(studentGroup, NAME);
    const lrsGroup = formLrsGroupName(mainGroup, findFormLrsGroupNumber(groups, mainGroup));

    return (
        <div>
            {SecureResultBanner}
            {SummerPeriodBanner}
            <div className="instruction-container">
                <aside className={`students-block columns-length-${columns.length}`}>
                    <StudentListTable
                        studentsData={studentsData}
                        selectedStudentId={selectedStudentId}
                        selectStudentId={selectItem}
                        additionalRowDataRenderer={commonCellBuilderComponent}
                        columns={columns}
                    />
                </aside>
                <StudentRecommendations
                    studentInfo={get(selectedStudentData, INFO)}
                    mainGroup={mainGroup}
                    additionalGroup={additionalGroup}
                    groupDescription={get(studentGroup, 'text.desc')}
                    observations={extractObservationsFromStatus(selectedStudentData)}
                    activities={additionalActivities}
                    observationsContent={observationsContent}
                    measureList={measureList}
                    observationActivitiesMapper={observationActivitiesMapper}
                    selectedGrade={selectedGrade}
                    staffId={staffId}
                    lrsTrackingInfo={lrsTrackingInfo}
                    onHelpIconClick={onHelpIconClick}
                    selectObservation={selectObservation}
                    sessionId={sessionId}
                    lastLocation={lastLocation}
                    lrsGroup={lrsGroup}
                    isSecure={isSecure}
                />
            </div>
        </div>
    );
};

Students.defaultProps = {
    studentsData: [],
    groups: [],
    columns: [],
    selectedGrade: null,
    observationsContent: [],
    GroupWidget: () => {},
    selectIndividualStudent: () => {},
    onHelpIconClick: () => {},
    selectObservation: () => {},
    measureList: [],
    observationActivitiesMapper: {},
    staffId: null,
    lrsTrackingInfo: {},
    location: {},
    sessionId: '',
    lastLocation: {},
    SecureResultBanner: <></>,
    SummerPeriodBanner: <></>,
    isSecure: false,
};

Students.propTypes = {
    studentsData: PropTypes.array,
    columns: PropTypes.array,
    groups: PropTypes.array,
    selectedGrade: PropTypes.string,
    observationsContent: PropTypes.array,
    GroupWidget: PropTypes.func,
    selectIndividualStudent: PropTypes.func,
    onHelpIconClick: PropTypes.func,
    selectObservation: PropTypes.func,
    measureList: PropTypes.array,
    observationActivitiesMapper: PropTypes.object,
    staffId: PropTypes.string,
    lrsTrackingInfo: PropTypes.object,
    location: PropTypes.object,
    commonCellBuilderComponent: PropTypes.func.isRequired,
    sessionId: PropTypes.string,
    lastLocation: PropTypes.object,
    SecureResultBanner: PropTypes.oneOfType([PropTypes.element, PropTypes.bool]),
    SummerPeriodBanner: PropTypes.oneOfType([PropTypes.element, PropTypes.bool]),
    isSecure: PropTypes.bool,
};

export default Students;
